import {
	createFileRoute,
	Link,
	Outlet,
	useNavigate,
} from "@tanstack/react-router";
import {
	Award,
	CalendarRange,
	FileImage,
	GraduationCap,
	LayoutDashboard,
	ListChecks,
	LogOut,
	Menu,
	Moon,
	Sun,
	UserPlus,
	Users2,
	X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { NotificationBell } from "#/components/admin/notification-bell";
import { LogoMark } from "#/components/logo";
import { type AdminTheme, useAdminTheme } from "#/lib/admin-theme";
import { authClient } from "#/lib/auth-client";
import {
	asRole,
	canOpenOrdersPage,
	canRegisterLeads,
	canViewDegrees,
	canViewEditions,
	canViewFiches,
	canViewLeads,
	canViewWeeklyReport,
	isAdmin,
} from "#/lib/roles";

export const Route = createFileRoute("/admin")({
	head: () => ({
		meta: [
			{ title: "لوحة التحكم | أكاديمية مستشير" },
			{ name: "robots", content: "noindex, nofollow" },
		],
	}),
	component: AdminLayout,
});

// `show` is asked per role rather than a flat adminOnly flag, so the community
// manager (who sees exactly one item) doesn't need a special case here.
const NAV_ITEMS = [
	{
		to: "/admin",
		label: "اللوحة الرئيسية",
		icon: LayoutDashboard,
		show: canViewLeads,
	},
	{
		to: "/admin/leads",
		label: "الطلبات",
		icon: ListChecks,
		show: canOpenOrdersPage,
	},
	{
		to: "/admin/register-lead",
		label: "تسجيل طلب",
		icon: UserPlus,
		show: canRegisterLeads,
	},
	// Trainings, their editions (the old scheduling page), each edition's report
	// and feedback form all live under this one entry; each role sees the parts
	// of it they may — see the gates in src/lib/roles.ts.
	{
		to: "/admin/trainings",
		label: "التدريبات",
		icon: GraduationCap,
		show: canViewEditions,
	},
	// Technical data sheets and the social media designs made from them — the
	// designer's only page.
	{
		to: "/admin/fiches",
		label: "البطاقات التقنية",
		icon: FileImage,
		show: canViewFiches,
	},
	// Each edition's student list and the certificates made for it — sales
	// write the list, the designer delivers.
	{
		to: "/admin/degrees",
		label: "الشهادات",
		icon: Award,
		show: canViewDegrees,
	},
	{
		to: "/admin/weekly-report",
		label: "التقرير الأسبوعي",
		icon: CalendarRange,
		show: canViewWeeklyReport,
	},
	{ to: "/admin/users", label: "المستخدمون", icon: Users2, show: isAdmin },
] as const;

function AdminLayout() {
	const { data: session, isPending } = authClient.useSession();
	const navigate = useNavigate();
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	// Called before the early returns below, so the login page is themed too.
	const { theme, toggle: toggleTheme } = useAdminTheme();

	// Session still resolving — avoid flashing the login card before an
	// existing cookie session comes back.
	if (isPending) {
		return <div className="min-h-screen bg-canvas" />;
	}

	if (!session?.user) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
				<Outlet />
			</div>
		);
	}

	const role = asRole(session.user.role);
	const visibleItems = NAV_ITEMS.filter((item) => item.show(role));
	// Notifications are about fiches and certificates: only their roles get a bell.
	const showBell = canViewFiches(role) || canViewDegrees(role);

	function handleSignOut() {
		void authClient.signOut().then(() => {
			void navigate({ to: "/admin/login" });
		});
	}

	return (
		<div className="flex min-h-screen bg-canvas">
			{/* Desktop sidebar */}
			<aside className="hidden w-64 shrink-0 flex-col border-e border-line bg-white md:flex print:hidden">
				<SidebarContent
					items={visibleItems}
					userName={session.user.name}
					onSignOut={handleSignOut}
					theme={theme}
					onToggleTheme={toggleTheme}
					headerAction={showBell ? <NotificationBell /> : null}
				/>
			</aside>

			{/* Mobile top bar + slide-over */}
			{/* min-w-0 at every width: without it this column grows to its widest
			    content (the orders table is 1000px), dragging the whole page wider
			    than a phone screen instead of letting the table scroll in its card. */}
			<div className="flex min-w-0 flex-1 flex-col">
				<header className="flex items-center justify-between border-b border-line bg-white px-4 py-3 md:hidden print:hidden">
					<Link to="/admin" className="flex items-center gap-2">
						<LogoMark className="h-7 w-auto" />
						<span className="text-sm font-bold text-navy">لوحة التحكم</span>
					</Link>
					<div className="flex items-center gap-1">
						{showBell ? <NotificationBell placement="header" /> : null}
						<button
							type="button"
							onClick={() => setMobileNavOpen(true)}
							className="rounded-lg p-2 text-navy hover:bg-canvas"
							aria-label="فتح القائمة"
						>
							<Menu className="h-5 w-5" />
						</button>
					</div>
				</header>

				{mobileNavOpen ? (
					<div className="fixed inset-0 z-50 flex md:hidden">
						<button
							type="button"
							aria-label="إغلاق القائمة"
							onClick={() => setMobileNavOpen(false)}
							className="absolute inset-0 bg-navy/40"
						/>
						<div className="relative flex w-72 max-w-[80vw] flex-col border-e border-line bg-white">
							<div className="flex items-center justify-end px-4 pt-4">
								<button
									type="button"
									onClick={() => setMobileNavOpen(false)}
									className="rounded-lg p-2 text-navy hover:bg-canvas"
									aria-label="إغلاق القائمة"
								>
									<X className="h-5 w-5" />
								</button>
							</div>
							<SidebarContent
								items={visibleItems}
								userName={session.user.name}
								onSignOut={handleSignOut}
								onNavigate={() => setMobileNavOpen(false)}
								theme={theme}
								onToggleTheme={toggleTheme}
							/>
						</div>
					</div>
				) : null}

				<main className="flex-1 px-4 py-8 sm:px-6 lg:px-10">
					<div className="mx-auto max-w-7xl">
						<Outlet />
					</div>
				</main>
			</div>
		</div>
	);
}

function SidebarContent({
	items,
	userName,
	onSignOut,
	onNavigate,
	theme,
	onToggleTheme,
	headerAction,
}: {
	items: readonly (typeof NAV_ITEMS)[number][];
	userName: string;
	onSignOut: () => void;
	onNavigate?: () => void;
	theme: AdminTheme;
	onToggleTheme: () => void;
	/** Sits beside the logo — the bell, on the desktop sidebar only. */
	headerAction?: ReactNode;
}) {
	return (
		<div className="flex h-full flex-col gap-6 p-4">
			<div className="flex items-center justify-between gap-2 pt-2">
				<Link to="/admin" className="flex items-center gap-2 px-2">
					<LogoMark className="h-8 w-auto" />
					<span className="text-sm font-bold text-navy">لوحة التحكم</span>
				</Link>
				{headerAction}
			</div>

			<nav className="flex flex-1 flex-col gap-1">
				{items.map((item) => (
					<Link
						key={item.to}
						to={item.to}
						onClick={onNavigate}
						activeOptions={{ exact: item.to === "/admin" }}
						className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-muted transition-colors hover:bg-canvas hover:text-navy [&.active]:bg-navy [&.active]:text-white"
					>
						<item.icon className="h-4.5 w-4.5 shrink-0" />
						{item.label}
					</Link>
				))}
			</nav>

			<div className="flex flex-col gap-2 border-t border-line pt-4">
				<p className="truncate px-2 text-xs font-semibold text-muted">
					{userName}
				</p>
				<button
					type="button"
					onClick={onToggleTheme}
					aria-pressed={theme === "dark"}
					className="inline-flex items-center gap-2 rounded-xl border border-line px-3 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-canvas"
				>
					{theme === "dark" ? (
						<Sun className="h-4 w-4" />
					) : (
						<Moon className="h-4 w-4" />
					)}
					{theme === "dark" ? "الوضع الفاتح" : "الوضع الداكن"}
				</button>
				<button
					type="button"
					onClick={onSignOut}
					className="inline-flex items-center gap-2 rounded-xl border border-line px-3 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-canvas"
				>
					<LogOut className="h-4 w-4" />
					تسجيل الخروج
				</button>
			</div>
		</div>
	);
}
